import React, { useState, useEffect, useRef } from 'react';
import {
  // useDispatch,
  useSelector,
} from 'react-redux';
import { getUserLoading, getUser } from '../../data/selectors/userAuthSelectors';
import ResultsSection from './results/ResultsSection';
import styles from './studies.module.css';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import createQuery, { emptyQuery } from './studiesFuncs/createQuery';
import ModalGroup from '../modalGroup/ModalGroup';
import {
  yearPublished,
  tumorType,
  conventionalTreatmentTypes,
  sideEffects,
} from '../../utils/dropdownSeedData';
import SynonymModal from '../modalGroup/SynonymModal';
import { getSynonymDictionaries } from '../../services/synonyms';
import SearchInfo from '../searchInfo/SearchInfo';
import executeSearch, { executeSearchPageChange } from './studiesFuncs/executeSearch';
import { TiFilter } from 'react-icons/ti';
import { getNotableStudies } from '../../services/studies';
// import { setQuery } from '../../data/actions/searchActions';

const filterGroupArrays = [yearPublished, tumorType, conventionalTreatmentTypes, sideEffects];
let naturalTherapyAgents = ['Natural Therapy Agents', 'All Natural Therapy Agents'];
let conventionalTreatmentAgents = [
  'Conventional Treatment Agents',
  'All Conventional Treatment Agents',
];

const initializeFilterState = () => {
  const baseState = filterGroupArrays.reduce((initialState, filterArray) => {
    const [filterName, allOption] = filterArray;
    return { ...initialState, [filterName]: [allOption] };
  }, {});

  // Initialize SynonymModal filters
  return {
    ...baseState,
    'Natural Therapy Agents': ['All Natural Therapy Agents'],
    'Conventional Treatment Agents': ['All Conventional Treatment Agents'],
  };
};

// Add RIS generation function
function generateRIS(results) {
  return results
    .map((result) => {
      // Determine reference type based on study type
      const typeMap = {
        'Systematic Review': 'MGZN', // Meta-analysis
        'Clinical Trial': 'JOUR', // Journal article
        Observational: 'JOUR',
        'Case Report': 'JOUR',
        'Support Literature': 'JOUR',
      };
      const type = typeMap[result.studyType] || 'JOUR';

      return [
        `TY  - ${type}`,
        `TI  - ${result.summaryTitle || ''}`,
        `AU  - ${(result.author || '').replace(/;/g, '\nAU  - ')}`,
        `PY  - ${result.year || ''}`,
        `AB  - ${result.abstract || ''}`,
        `KW  - ${result.keywords ? result.keywords.replace(/,/g, '\nKW  - ') : ''}`,
        `UR  - ${result.link || ''}`,
        'ER  - ',
      ].join('\n');
    })
    .join('\n\n');
}

// Add download handler
function downloadRIS(results) {
  const risContent = generateRIS(results);
  const blob = new Blob([risContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'search-results.ris';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const Studies = () => {
  const userLoading = useSelector(getUserLoading);
  const user = useSelector(getUser);
  // const dispatch = useDispatch();
  const [filterOptions, setFilterOptions] = useState(initializeFilterState);
  const [searchText, setSearchText] = useState('');
  const [masterSearchQuery, setMasterSearchQuery] = useState({});
  const [studiesLoading, setStudiesLoading] = useState({ loading: false, newSearch: false });
  const [pediatricCheckbox, setPediatricCheckbox] = useState(false);
  const [precancerFilter, setPrecancerFilter] = useState({
    precancerOnly: false,
    excludePrecancer: false,
  });
  const betaSearch = false;
  const [remountKey, setRemountKey] = useState(0);
  const [searchOccuredFlag, setSearchOccuredFlag] = useState(false);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [dictionariesLoading, setDictionariesLoading] = useState(false);
  const [naturalSynonymsDictionary, setNaturalSynonymsDictionary] = useState({});
  const [conventionalSynonymsDictionary, setConventionalSynonymsDictionary] = useState({});
  const [studyGroups, setStudyGroups] = useState({
    1: { results: [], fullCount: 0, page: 1 },
    2: { results: [], fullCount: 0, page: 1 },
    3: { results: [], fullCount: 0, page: 1 },
    4: { results: [], fullCount: 0, page: 1 },
    5: { results: [], fullCount: 0, page: 1 },
  });
  const [supportLit, setSupportLit] = useState({
    results: [],
    fullCount: 0,
    page: 1,
  });
  const [pageChangeLoading, setPageChangeLoading] = useState({});
  const [searchErrors, setSearchErrors] = useState({});

  useEffect(() => {
    document.title = 'KNOW - Studies';
  }, []);

  useEffect(() => {
    setDictionariesLoading(true);
    getSynonymDictionaries()
      .then(({ data: { naturalSynonymsDictionary, conventionalSynonymsDictionary } }) => {
        naturalTherapyAgents = [
          'Natural Therapy Agents',
          'All Natural Therapy Agents',
          ...Object.keys(naturalSynonymsDictionary).map((key) =>
            key.includes('"') ? key.slice(1, -1) : key
          ),
        ];
        conventionalTreatmentAgents = [
          'Conventional Treatment Agents',
          'All Conventional Treatment Agents',
          ...Object.keys(conventionalSynonymsDictionary).map((key) =>
            key.includes('"') ? key.slice(1, -1) : key
          ),
        ];
        setNaturalSynonymsDictionary(naturalSynonymsDictionary);
        setConventionalSynonymsDictionary(conventionalSynonymsDictionary);
        setDictionariesLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setDictionariesLoading(false);
      });
  }, []);

  const initialRender = useRef(true);

  function handleSearchResults(summarizedRes, supportRes) {
    if (summarizedRes) {
      const grouped = { 1: [], 2: [], 3: [], 4: [], 5: [] };
      summarizedRes.results.forEach((study) => {
        const type = study.studyTypeInt || 5;
        if (grouped[type]) grouped[type].push(study);
      });
      setStudyGroups({
        1: { results: grouped[1], fullCount: summarizedRes.systematicCount ?? 0, page: 1 },
        2: { results: grouped[2], fullCount: summarizedRes.clinicalCount ?? 0, page: 1 },
        3: { results: grouped[3], fullCount: summarizedRes.observationalCount ?? 0, page: 1 },
        4: { results: grouped[4], fullCount: summarizedRes.caseCount ?? 0, page: 1 },
        5: { results: grouped[5], fullCount: summarizedRes.unknownCount ?? 0, page: 1 },
      });
    }
    if (supportRes) {
      setSupportLit({ results: supportRes.results, fullCount: supportRes.fullCount, page: 1 });
    }
  }

  useEffect(() => {
    if (!initialRender.current && updateFlag) {
      setStudiesLoading({ loading: true });
      clearStudies();
      executeSearch(
        masterSearchQuery,
        (summarizedRes) => handleSearchResults(summarizedRes, null),
        null,
        betaSearch
      ).then(() => {
        setStudiesLoading({ loading: false });
        setUpdateFlag(false);
      });
    }
  }, [updateFlag]);

  function handleSubmit({ event = null, showSupportLit = false, newSearch = false }) {
    if (event) {
      event.preventDefault();
    }
    clearStudies();
    const searchQuery = createQuery(
      filterOptions,
      searchText,
      pediatricCheckbox,
      betaSearch,
      precancerFilter
    );
    if (searchQuery !== emptyQuery) {
      setStudiesLoading({ loading: true, newSearch });
      setMasterSearchQuery(searchQuery);
      // dispatch(setQuery(searchQuery));
      executeSearch(
        searchQuery,
        (summarizedRes) => handleSearchResults(summarizedRes, null),
        showSupportLit ? (supportRes) => handleSearchResults(null, supportRes) : null,
        betaSearch
      ).then(() => {
        setSearchOccuredFlag(true);
        setStudiesLoading({ loading: false, newSearch });
      });
    }
  }

  useEffect(() => {
    if (!initialRender.current) {
      handleSubmit({
        event: null,
        showSupportLit:
          !pediatricCheckbox && user.role !== 'allied' && user.organizations !== 'ccnm',
        newSearch: true,
      });
    } else {
      initialRender.current = false;
    }
  }, [filterOptions, pediatricCheckbox, precancerFilter]);

  const updateStudiesCallback = () => setUpdateFlag(true);

  if (dictionariesLoading || userLoading) return <LoadingSpinner />;

  const url = new URL(window.location);
  if (url.searchParams.get('notable') === 'true') {
    window.history.pushState('', document.title, window.location.pathname);
    setStudiesLoading({ loading: true, newSearch: true });
    setSearchOccuredFlag(true);
    getNotableStudies()
      .then((res) => {
        const grouped = { 1: [], 2: [], 3: [], 4: [], 5: [] };
        res.data.forEach((study) => {
          const type = study.studyTypeInt || 5;
          if (grouped[type]) grouped[type].push(study);
        });
        setStudyGroups({
          1: { results: grouped[1], fullCount: grouped[1].length, page: 1 },
          2: { results: grouped[2], fullCount: grouped[2].length, page: 1 },
          3: { results: grouped[3], fullCount: grouped[3].length, page: 1 },
          4: { results: grouped[4], fullCount: grouped[4].length, page: 1 },
          5: { results: grouped[5], fullCount: grouped[5].length, page: 1 },
        });
      })
      .catch((err) => console.error(err))
      .finally(setStudiesLoading({ loading: false, newSearch: false }));
  }

  function clearStudies() {
    setStudyGroups({
      1: { results: [], fullCount: 0, page: 1 },
      2: { results: [], fullCount: 0, page: 1 },
      3: { results: [], fullCount: 0, page: 1 },
      4: { results: [], fullCount: 0, page: 1 },
      5: { results: [], fullCount: 0, page: 1 },
    });
    setSupportLit({ results: [], fullCount: 0, page: 1 });
    setSearchErrors({});
    setPageChangeLoading({});
    window.history.pushState('', document.title, window.location.pathname);
  }

  const handlePediatricChange = () => setPediatricCheckbox(!pediatricCheckbox);
  const handlePrecancerOnlyChange = () =>
    setPrecancerFilter({
      precancerOnly: !precancerFilter.precancerOnly,
      excludePrecancer: false,
    });
  const handleExcludePrecancerChange = () =>
    setPrecancerFilter({
      precancerOnly: false,
      excludePrecancer: !precancerFilter.excludePrecancer,
    });
  const handleSearchText = ({ target }) => setSearchText(target.value);
  const handleClear = () => {
    setRemountKey(remountKey + 1);
    setSearchOccuredFlag(false);
    clearStudies();
    setSearchText('');
    initialRender.current = true;
    setPediatricCheckbox(false);
    setPrecancerFilter({ precancerOnly: false, excludePrecancer: false });
    setFilterOptions(initializeFilterState());
  };
  const handleFilterChange = (newOptions) =>
    setFilterOptions((prev) => ({ ...prev, ...newOptions }));

  function handlePageChange(groupType, newPage) {
    setPageChangeLoading((prev) => ({ ...prev, [groupType]: true }));
    setSearchErrors((prev) => ({ ...prev, [groupType]: null }));

    // Optimistically update page number for immediate UI feedback
    if (groupType === 'support') {
      setSupportLit((prev) => ({ ...prev, page: newPage }));
    } else {
      setStudyGroups((prev) => ({
        ...prev,
        [groupType]: { ...prev[groupType], page: newPage },
      }));
    }

    executeSearchPageChange(
      masterSearchQuery,
      groupType,
      newPage,
      (res) => {
        setPageChangeLoading((prev) => ({ ...prev, [groupType]: false }));
        if (res === null) {
          setSearchErrors((prev) => ({ ...prev, [groupType]: 'Failed to load page' }));
          if (groupType === 'support') {
            setSupportLit((prev) => ({ ...prev, page: newPage > 1 ? newPage - 1 : 1 }));
          } else {
            setStudyGroups((prev) => ({
              ...prev,
              [groupType]: {
                ...prev[groupType],
                page: newPage > 1 ? newPage - 1 : 1,
              },
            }));
          }
        } else {
          if (groupType === 'support') {
            setSupportLit({ results: res.results, fullCount: res.fullCount, page: newPage });
          } else {
            setStudyGroups((prev) => ({
              ...prev,
              [groupType]: { results: res.results, fullCount: res.fullCount, page: newPage },
            }));
          }
        }
      },
      betaSearch
    );
  }

  const filteredKeys = Object.entries(filterOptions)
    .filter(([, [firstFilter]]) => !firstFilter.startsWith('All '))
    .map(([key]) => key);

  const {
    searchContainer,
    submit,
    textInput,
    searchButtonContainer,
    searchButton,
    checkbox,
    checkboxContainer,
    filtersColumn,
  } = styles;
  return (
    <div role="main" aria-label="search for studies">
      <section className={searchContainer} aria-label="search options">
        <form
          className={submit}
          onSubmit={(e) =>
            handleSubmit({
              event: e,
              showSupportLit:
                !pediatricCheckbox && user.role !== 'allied' && user.organizations !== 'ccnm',
              newSearch: true,
            })
          }
        >
          <span>
            <SearchInfo />
            Search:
          </span>
          <input
            type="text"
            className={textInput}
            value={searchText}
            onChange={handleSearchText}
            autoFocus={true}
          />
          <div className={searchButtonContainer}>
            <button
              type="submit"
              className={searchButton}
              value="Search"
              aria-label="search button"
            >
              Search
            </button>
            <button
              type="button"
              className={searchButton}
              onClick={handleClear}
              aria-label="clear search results"
            >
              Clear
            </button>
          </div>
        </form>
        <div className={filtersColumn}>
          <div className={checkboxContainer}>
            <label>
              <input
                className={checkbox}
                type="checkbox"
                checked={pediatricCheckbox}
                onChange={handlePediatricChange}
              />
              Pediatric
              <TiFilter />
            </label>
            <label>
              <input
                className={checkbox}
                type="checkbox"
                checked={precancerFilter.precancerOnly}
                onChange={handlePrecancerOnlyChange}
              />
              Precancer
              <TiFilter />
            </label>
            <label>
              <input
                className={checkbox}
                type="checkbox"
                checked={precancerFilter.excludePrecancer}
                onChange={handleExcludePrecancerChange}
              />
              Exclude Precancer
              <TiFilter />
            </label>
          </div>
          <ModalGroup
            key={remountKey}
            modalGroupArray={filterGroupArrays}
            setCallback={handleFilterChange}
            includeAllOption={true}
            styles={styles}
          >
            <SynonymModal
              key={naturalTherapyAgents[0]}
              optionsArray={naturalTherapyAgents}
              includeAllOption={true}
              synonymsDictionary={naturalSynonymsDictionary}
              setCallback={(selection) => setFilterOptions((prev) => ({ ...prev, ...selection }))}
            />
            <SynonymModal
              key={conventionalTreatmentAgents[0]}
              optionsArray={conventionalTreatmentAgents}
              includeAllOption={true}
              synonymsDictionary={conventionalSynonymsDictionary}
              setCallback={(selection) => setFilterOptions((prev) => ({ ...prev, ...selection }))}
            />
          </ModalGroup>
        </div>
      </section>
      {searchOccuredFlag && (
        <ResultsSection
          studyGroups={studyGroups}
          filteredKeys={filteredKeys}
          supportLit={supportLit}
          updateCallback={updateStudiesCallback}
          masterSearchQuery={masterSearchQuery}
          searchOccuredFlag={searchOccuredFlag}
          studiesLoading={studiesLoading}
          onPageChange={handlePageChange}
          pageChangeLoading={pageChangeLoading}
          searchErrors={searchErrors}
          onDownload={() => downloadRIS(Object.values(studyGroups).flatMap((g) => g.results))}
        />
      )}
      {!searchOccuredFlag && <p className={styles.placeholder}>Search results will appear here.</p>}
    </div>
  );
};

export default Studies;
